/**
 * GraphQL mutation hook for the customer portal.
 *
 * Similar to useGraphQL but for mutations — returns a trigger function
 * instead of auto-executing on mount.
 */

import { useCallback, useState } from 'react';

import { usePortalAuth } from './usePortalAuth.js';

interface GraphQLResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

interface UseGraphQLMutationResult<T> {
  mutate: (variables: Record<string, unknown>) => Promise<T | null>;
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useGraphQLMutation<T>(mutation: string): UseGraphQLMutationResult<T> {
  const token = usePortalAuth((state) => state.token);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (variables: Record<string, unknown>): Promise<T | null> => {
      setLoading(true);
      setError(null);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token !== null) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      try {
        const res = await fetch('/graphql', {
          method: 'POST',
          headers,
          body: JSON.stringify({ query: mutation, variables }),
        });

        if (!res.ok) {
          const msg = `Request failed: ${String(res.status)} ${res.statusText}`;
          setError(msg);
          setLoading(false);
          return null;
        }

        const json = (await res.json()) as GraphQLResponse<T>;

        if (json.errors !== undefined && json.errors.length > 0) {
          setError(json.errors.map((e) => e.message).join('; '));
          setLoading(false);
          return null;
        }

        const result = json.data ?? null;
        setData(result);
        setLoading(false);
        return result;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Network error';
        setError(msg);
        setLoading(false);
        return null;
      }
    },
    [mutation, token],
  );

  return { mutate, data, loading, error };
}
